import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import {
  getChildAndYouthFieldMappings,
  getStudentTravelFieldMappings,
  FieldMapping,
} from '@/components/Import/fieldsLegendData';
import { StudentFormType } from '@/hooks/useStudentFormSchema';
import { OptionsType } from '@/components/core/Dropdown';
import { useHalfProductAvailability } from '@/hooks/useHalfProductAvailability';

export type FieldMappingOption = OptionsType & {
  required?: boolean;
};

export const DO_NOT_USE_ID = 'do_not_use';

export const normalizeImportHeader = (header: string) =>
  header.toLowerCase().trim();

export const useFieldMappings = (type: StudentFormType) => {
  const { formatMessage } = useIntl();
  const isHalfProductAvailable = useHalfProductAvailability();
  const disableHalfProduct = !isHalfProductAvailable(type);

  const mappings = useMemo((): FieldMapping[] => {
    switch (type) {
      case 'child-and-youth':
        return getChildAndYouthFieldMappings(disableHalfProduct);
      case 'travels':
      case 'transportation':
      default:
        return getStudentTravelFieldMappings(disableHalfProduct);
    }
  }, [type, disableHalfProduct]);

  const toDropdownOptions = useMemo((): FieldMappingOption[] => {
    const options: FieldMappingOption[] = mappings.map((m) => ({
      id: m.apiField,
      label: m.column,
      value: m.apiField,
      required: m.required,
      description: m.required
        ? formatMessage({ defaultMessage: 'Obligatorisk' })
        : undefined,
    }));

    options.push({
      id: DO_NOT_USE_ID,
      label: formatMessage({ defaultMessage: 'Använd inte' }),
      value: DO_NOT_USE_ID,
      description: formatMessage({
        defaultMessage: 'Fältet kommer inte användas vid importen',
      }),
    });

    return options;
  }, [mappings, formatMessage]);

  const findMatchingField = useMemo(() => {
    return (header: string): FieldMapping | undefined => {
      const normalizedHeader = normalizeImportHeader(header);
      return mappings.find(
        (m) => normalizeImportHeader(m.column) === normalizedHeader,
      );
    };
  }, [mappings]);

  const getRequiredFields = useMemo(() => {
    return mappings.filter((m) => m.required);
  }, [mappings]);

  return {
    mappings,
    toDropdownOptions,
    findMatchingField,
    getRequiredFields,
    DO_NOT_USE_ID,
  };
};
